import { incomeTypeCodes } from "../codegen/incomeTypeCodes";
import { IncomeTypeMetadata } from "./model";
import { Translations } from "./Translations";
import { TransactionCode, Language } from "../model";


/**
 * Provides business logic for Income types in Incomes Registry (Tulorekisteri).
 */
export class IncomeTypesLogic {
  /**
   * Searches the income type texts (supportedTypes) using the current language version.
   * Returns all types if the search string is less than 2 chars.
   * @param searchString Search text - will be used as case-insensitive. Will be trimmed. Minimum length 2.
   */
  public static search(searchString: string): IncomeTypeMetadata[] {
    const supportedTypes = IncomeTypesLogic.getAll().filter((x) => x.isSupported);
    if (!searchString || searchString.trim().length < 2) {
      return supportedTypes;
    }
    searchString = searchString.trim().toLowerCase();
    return supportedTypes.filter((x) => x.code.toString().indexOf(searchString) >= 0
      || x.label.toLowerCase().indexOf(searchString) >= 0);
  }

  /**
   * Gets all codes in Incomes register reporting.
   * This also includes the Salaxy internal codes eg. 1: "Ignored" / "Not reported"
   * For normal language-versioned behavior use cached allTypes field.
   * @param language Language for the label.
   */
  public static getAll(language: Language = null): IncomeTypeMetadata[] {
    language = language || Translations.getLanguage() as any || Language.Fi;
    const allCodes: IncomeTypeMetadata[]  = [{
      code: 1,
      label: (language === Language.En ? "No IR reporting" : (language === Language.Sv ? "Ingen IR reporting" : "Ei ilmoiteta tulorekisteriin")),
      isNegativeSupported: true,
      pensionInsurance: false,
      accidentInsurance: false,
      unemploymentInsurance: false,
      healthInsurance: false,
      insuranceInformationAllowed: true,
      taxDefault: 0,
      notSupportedError: null,
      description: Translations.get("SALAXY.CORE.IncomeTypesLogic.incomeTypeMetaData.desription"),
      taxAndSidecostsDescr: Translations.get("SALAXY.CORE.IncomeTypesLogic.incomeTypeMetaData.taxAndSidecostsDescr"),
      isSupported: true,
      paymentDefault: 1,
      calcGrouping: "expenses",
    }].concat(this.getOriginal(language));
    return allCodes;
  }

  /**
   * Gets the original codes in Incomes register.
   * This does not include virtual codes, eg. 1: "Ignored" / "Not reported"
   * For normal language-versioned behavior use cached originalTypes field.
   * @param language Language for the label. Typically use null for automatic language versioning.
   */
  private static getOriginal(language: Language = null): IncomeTypeMetadata[] {
    return incomeTypeCodes.map((x) => ({
      code: x.code,
      label: (language === Language.En ? x.nameEn : (language === Language.Sv ? x.nameSv : x.nameFi)),
      isNegativeSupported: x.isNegativeSupported,
      pensionInsurance: x.pensionInsurance,
      accidentInsurance: x.accidentInsurance,
      unemploymentInsurance: x.unemploymentInsurance,
      healthInsurance: x.healthInsurance,
      insuranceInformationAllowed: x.insuranceInformationAllowed,
      taxDefault: x.taxDefault,
      notSupportedError: x.notSupportedError,
      description: (language === Language.En ? x.descriptionEn : (language === Language.Sv ? x.descriptionSv : x.descriptionFi)),
      taxAndSidecostsDescr: (language === Language.En ? x.taxAndSideCostsDescrEn : (language === Language.Sv ? x.taxAndSideCostsDescrSv : x.taxAndSideCostsDescrFi)),
      isSupported: x.isSupported,
      paymentDefault: x.paymentDefault,
      calcGrouping: x.calcGrouping,
      transactionCode: x.transactionCode as TransactionCode,
    }));
  }
}
